import { __assign, __makeTemplateObject, __rest } from "tslib";
import React, { useCallback } from 'react';
import { css, cx } from 'emotion';
import { List } from '../List/List';
import { styleMixins, useStyles } from '../../themes';
import { Icon } from '../Icon/Icon';
/** @internal */
export var Menu = React.forwardRef(function (_a, ref) {
    var header = _a.header, items = _a.items, onClose = _a.onClose, otherProps = __rest(_a, ["header", "items", "onClose"]);
    var styles = useStyles(getMenuStyles);
    var onClick = useCallback(function () {
        if (onClose) {
            onClose();
        }
    }, [onClose]);
    return (React.createElement("div", __assign({}, otherProps, { ref: ref, className: styles.wrapper }),
        header && React.createElement("div", { className: styles.header }, header),
        React.createElement(List, { items: items || [], renderItem: function (item) {
                return React.createElement(MenuGroup, { group: item, onClick: onClick });
            } })));
});
Menu.displayName = 'Menu';
var MenuGroup = function (_a) {
    var group = _a.group, onClick = _a.onClick;
    var styles = useStyles(getMenuStyles);
    if (group.items.length === 0) {
        return null;
    }
    return (React.createElement("div", null,
        group.label && React.createElement("div", { className: styles.groupLabel }, group.label),
        React.createElement(List, { items: group.items || [], renderItem: function (item) {
                return (React.createElement(MenuItemComponent, { url: item.url, label: item.label, target: item.target, icon: item.icon, active: item.active, onClick: function (e) {
                        // We can have both url and onClick and we want to allow user to open the link in new tab/window
                        var isSpecialKeyPressed = e.ctrlKey || e.metaKey || e.shiftKey;
                        if (isSpecialKeyPressed && item.url) {
                            return;
                        }
                        if (item.onClick) {
                            e.preventDefault();
                            item.onClick(e);
                        }
                        // Typically closes the context menu
                        if (onClick) {
                            onClick();
                        }
                    } }));
            } })));
};
MenuGroup.displayName = 'MenuGroup';
var MenuItemComponent = React.memo(function (_a) {
    var _b;
    var url = _a.url, icon = _a.icon, label = _a.label, target = _a.target, onClick = _a.onClick, className = _a.className, active = _a.active;
    var styles = useStyles(getMenuStyles);
    var itemStyle = cx((_b = {},
        _b[styles.item] = true,
        _b[styles.activeItem] = active,
        _b), className);
    return (React.createElement("div", { className: itemStyle },
        React.createElement("a", { href: url ? url : undefined, target: target, className: styles.link, onClick: onClick, rel: target === '_blank' ? 'noopener noreferrer' : undefined },
            icon && React.createElement(Icon, { name: icon, className: styles.icon }),
            " ",
            label)));
});
MenuItemComponent.displayName = 'MenuItemComponent';
var getMenuStyles = function (theme) {
    var linkColor = theme.colors.text;
    var linkColorHover = theme.colors.linkHover;
    var wrapperBg = theme.colors.formInputBg;
    var wrapperShadow = theme.isDark ? theme.palette.black : theme.palette.gray3;
    var groupLabelColor = theme.colors.textWeak;
    var itemBgHover = styleMixins.hoverColor(theme.colors.bg1, theme);
    var headerBg = theme.colors.formInputBg;
    var headerSeparator = theme.colors.border3;
    return {
        header: css(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n      padding: 4px;\n      border-bottom: 1px solid ", ";\n      background: ", ";\n      margin-bottom: ", ";\n      border-radius: ", " ", " 0 0;\n    "], ["\n      padding: 4px;\n      border-bottom: 1px solid ", ";\n      background: ", ";\n      margin-bottom: ", ";\n      border-radius: ", " ", " 0 0;\n    "])), headerSeparator, headerBg, theme.spacing.xs, theme.border.radius.sm, theme.border.radius.sm),
        wrapper: css(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n      background: ", ";\n      box-shadow: 0 2px 5px 0 ", ";\n      display: inline-block;\n      border-radius: ", ";\n    "], ["\n      background: ", ";\n      box-shadow: 0 2px 5px 0 ", ";\n      display: inline-block;\n      border-radius: ", ";\n    "])), wrapperBg, wrapperShadow, theme.border.radius.sm),
        link: css(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n      color: ", ";\n      display: flex;\n      cursor: pointer;\n      padding: 5px 12px 5px 10px;\n\n      &:hover {\n        color: ", ";\n        text-decoration: none;\n      }\n    "], ["\n      color: ", ";\n      display: flex;\n      cursor: pointer;\n      padding: 5px 12px 5px 10px;\n\n      &:hover {\n        color: ", ";\n        text-decoration: none;\n      }\n    "])), linkColor, linkColorHover),
        item: css(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n      background: none;\n      border-left: 2px solid transparent;\n      cursor: pointer;\n      white-space: nowrap;\n\n      &:hover {\n        background: ", ";\n        border-image: linear-gradient(#f05a28 30%, #fbca0a 99%);\n        border-image-slice: 1;\n      }\n    "], ["\n      background: none;\n      border-left: 2px solid transparent;\n      cursor: pointer;\n      white-space: nowrap;\n\n      &:hover {\n        background: ", ";\n        border-image: linear-gradient(#f05a28 30%, #fbca0a 99%);\n        border-image-slice: 1;\n      }\n    "])), itemBgHover),
        activeItem: css(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n      background: ", ";\n    "], ["\n      background: ", ";\n    "])), theme.colors.bg2),
        groupLabel: css(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n      color: ", ";\n      font-size: ", ";\n      line-height: ", ";\n      padding: ", " ", ";\n    "], ["\n      color: ", ";\n      font-size: ", ";\n      line-height: ", ";\n      padding: ", " ", ";\n    "])), groupLabelColor, theme.typography.size.sm, theme.typography.lineHeight.md, theme.spacing.xs, theme.spacing.sm),
        icon: css(templateObject_7 || (templateObject_7 = __makeTemplateObject(["\n      opacity: 0.7;\n      margin-right: 10px;\n      color: ", ";\n    "], ["\n      opacity: 0.7;\n      margin-right: 10px;\n      color: ", ";\n    "])), theme.colors.linkDisabled),
    };
};
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6, templateObject_7;
//# sourceMappingURL=Menu.js.map